import { useState, useRef } from "react";
import { CheckCircle2, XCircle, Upload, AlertTriangle, FileText, Trash2, Eye, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const STATUT_ICONE = {
  a_faire: { icon: XCircle, couleur: "#9CA3AF", label: "À faire" },
  fourni: { icon: Upload, couleur: "#0369A1", label: "Fourni, non validé" },
  valide: { icon: CheckCircle2, couleur: "#16A34A", label: "Validé" },
  non_applicable: { icon: XCircle, couleur: "#6B7280", label: "N/A" },
  derogation: { icon: AlertTriangle, couleur: "#B45309", label: "Dérogation" },
};

const CATEGORIES = [
  { key: "pieces_client", label: "Pièces client" },
  { key: "dossier_commercial", label: "Dossier commercial" },
  { key: "specifique_vn", label: "Spécifique VN" },
  { key: "specifique_vo", label: "Spécifique VO" },
];

// Chantier 3a : glisser-déposer directement sur chaque ligne de checklist.
export default function ChecklistPieces({ dossierId, checklist, canEdit, onMaj }) {
  const [derog, setDerog] = useState({ code: null, motif: "" });
  const [saveOn, setSaveOn] = useState(null);
  const [preview, setPreview] = useState(null);

  const patchChecklist = async (code, statut, motif = null) => {
    setSaveOn(code);
    try {
      const r = await api.patch(`/dossiers/${dossierId}/checklist/${code}`, { statut, motif_derogation: motif });
      onMaj(r.data);
      toast.success(`${code} → ${STATUT_ICONE[statut]?.label || statut}`);
      setDerog({ code: null, motif: "" });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaveOn(null); }
  };

  const parCategorie = CATEGORIES.map((c) => ({
    ...c, items: (checklist || []).filter((it) => it.categorie === c.key),
  })).filter((c) => c.items.length);

  return (
    <div data-testid="checklist-pieces">
      {parCategorie.map((cat) => (
        <div key={cat.key} className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[--idc-muted] mb-1.5">{cat.label}</div>
          <ul className="space-y-1">
            {cat.items.map((it) => (
              <LigneChecklist key={it.code} item={it} dossierId={dossierId} canEdit={canEdit}
                saveOn={saveOn} onPatch={patchChecklist} onDerog={() => setDerog({ code: it.code, motif: "" })}
                onMaj={onMaj} onPreview={setPreview} />
            ))}
          </ul>
        </div>
      ))}

      {derog.code && (
        <Dialog open onOpenChange={(o) => !o && setDerog({ code: null, motif: "" })}>
          <DialogContent className="max-w-lg" data-testid="derog-dialog">
            <DialogHeader>
              <DialogTitle>Dérogation — {derog.code}</DialogTitle>
              <DialogDescription>Toute dérogation est <b>tracée dans l'audit</b> et attribuée à votre compte.</DialogDescription>
            </DialogHeader>
            <textarea rows={3} value={derog.motif} onChange={(e) => setDerog({ ...derog, motif: e.target.value })}
              placeholder="Motif obligatoire (ex: pièce fournie à la livraison, engagement client)"
              className="w-full text-[13px] border border-[--idc-border] rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
              data-testid="derog-motif" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDerog({ code: null, motif: "" })}>Annuler</Button>
              <Button onClick={() => patchChecklist(derog.code, "derogation", derog.motif)}
                disabled={!derog.motif.trim()} data-testid="derog-confirmer">Enregistrer la dérogation</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {preview && <PreviewPiece piece={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function LigneChecklist({ item: it, dossierId, canEdit, saveOn, onPatch, onDerog, onMaj, onPreview }) {
  const S = STATUT_ICONE[it.statut] || STATUT_ICONE.a_faire;
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format non autorisé (PDF, JPEG, PNG ou WEBP)."); return;
    }
    if (file.size > 8 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 8 Mo)."); return; }
    setProgress(5);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onprogress = (e) => e.lengthComputable && setProgress(Math.round((e.loaded / e.total) * 40));
        rd.onload = () => res(rd.result.split(",")[1]);
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      setProgress(50);
      await api.post(`/dossiers/${dossierId}/pieces-jointes`, {
        code_checklist: it.code, nom_fichier: file.name, type_mime: file.type, contenu_base64: b64,
      }, { onUploadProgress: (e) => e.total && setProgress(50 + Math.round((e.loaded / e.total) * 50)) });
      setProgress(100);
      toast.success(`${file.name} déposé — pièce « fournie ». À vous de la valider.`);
      const r = await api.get(`/dossiers/${dossierId}/administratif`);
      onMaj(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTimeout(() => setProgress(null), 400); }
  };

  const voir = async () => {
    try {
      const r = await api.get(`/pieces-jointes/${it.piece_id}`);
      onPreview(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const supprimer = async () => {
    try {
      await api.delete(`/pieces-jointes/${it.piece_id}`);
      toast.success("Pièce supprimée — ligne repassée « à faire ».");
      const r = await api.get(`/dossiers/${dossierId}/administratif`);
      onMaj(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <li
      onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { if (canEdit) { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); } }}
      className={`rounded px-2 py-1.5 transition-colors ${drag ? "bg-[#DBEAFE] ring-2 ring-[#2F6FED]/50" : "hover:bg-[#F8F9FB]"}`}
      data-testid={`checklist-${it.code}`}>
      <div className="flex items-center gap-2.5">
        <S.icon className="w-4 h-4 shrink-0" style={{ color: S.couleur }} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px]">{it.libelle}
            {it.obligatoire && <span className="text-[10px] text-[#B91C1C] font-bold ml-1">*</span>}
            {it.bloquant_livraison && <span className="text-[9.5px] px-1 py-0.5 rounded bg-[#FFE4E6] text-[#9F1239] ml-1.5 font-semibold">Bloque LIV.</span>}
            {it.bloquant_facturation && <span className="text-[9.5px] px-1 py-0.5 rounded bg-[#FED7AA] text-[#9A3412] ml-1.5 font-semibold">Bloque FAC.</span>}
          </div>
          <div className="text-[10.5px] text-[--idc-muted]">
            {S.label}{it.valide_par && ` · par ${it.valide_par}`}{it.motif_derogation && ` · dérogation : ${it.motif_derogation}`}
          </div>
        </div>
        {it.piece_id && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={voir} title="Prévisualiser la pièce"
              className="p-1 rounded hover:bg-[#DBEAFE] text-[#0369A1]" data-testid={`piece-voir-${it.code}`}>
              <FileText className="w-4 h-4" />
            </button>
            {canEdit && (
              <button onClick={supprimer} title="Supprimer la pièce"
                className="p-1 rounded hover:bg-[#FEE2E2] text-[#B91C1C]" data-testid={`piece-supprimer-${it.code}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => inputRef.current?.click()} title="Déposer une pièce (ou glisser-déposer sur la ligne)"
              className="p-1 rounded hover:bg-[#F1F4F9] text-[--idc-muted]" data-testid={`piece-parcourir-${it.code}`}>
              {progress != null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </button>
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
              data-testid={`piece-input-${it.code}`} />
            <select value={it.statut} disabled={saveOn === it.code}
              onChange={(e) => {
                const st = e.target.value;
                if (st === "derogation") { onDerog(); return; }
                onPatch(it.code, st);
              }}
              className="text-[11px] border border-[--idc-border] rounded px-1.5 py-0.5 bg-white"
              data-testid={`checklist-select-${it.code}`}>
              <option value="a_faire">À faire</option>
              <option value="fourni">Fourni</option>
              <option value="valide">Validé</option>
              <option value="non_applicable">N/A</option>
              <option value="derogation">Dérogation…</option>
            </select>
          </div>
        )}
      </div>
      {progress != null && (
        <div className="mt-1 h-1 rounded bg-[#E5E7EB] overflow-hidden" data-testid={`piece-progress-${it.code}`}>
          <div className="h-full bg-[#2F6FED] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </li>
  );
}

function PreviewPiece({ piece, onClose }) {
  const src = `data:${piece.type_mime};base64,${piece.contenu_base64}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="piece-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> {piece.nom_fichier}</DialogTitle>
          <DialogDescription>
            Déposée par {piece.depose_par} le {(piece.depose_le || "").slice(0, 10)} · {(piece.taille / 1024).toFixed(0)} Ko
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[70vh]">
          {piece.type_mime === "application/pdf" ? (
            <iframe src={src} title={piece.nom_fichier} className="w-full h-[65vh] border-0 rounded" />
          ) : (
            <img src={src} alt={piece.nom_fichier} className="max-w-full rounded" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
